import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/auth/LoadingSpinner';
import { useAdminSettings } from '@/hooks/admin/useAdminSettings';
import { useToast } from '@/hooks/use-toast';
import { 
  Settings as SettingsIcon, 
  Shield, 
  Bell, 
  Mail, 
  Database, 
  Users, 
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const AdminSettings = () => {
  const { settings, isLoading, isSaving, saveSettings, sendTestEmail, clearAllUserSessions } = useAdminSettings();
  const { toast } = useToast();
  const [formData, setFormData] = useState<typeof settings>(null);

  // Update form data when settings load
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!settings || !formData) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-destructive">Failed to load settings</p>
        </div>
      </AdminLayout>
    );
  }

  const handleSave = () => {
    saveSettings(formData);
  };

  const handleReset = () => {
    if (settings) {
      setFormData(settings);
      toast({
        title: 'Settings Reset',
        description: 'Form has been reset to saved values'
      });
    }
  };

  const updateFormData = (key: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev!, [key]: value }));
  };

  const confirmClearSessions = () => {
    if (window.confirm('Are you sure you want to clear all user sessions? This will log out all users.')) {
      clearAllUserSessions();
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Settings</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={sendTestEmail}>
              <Mail className="h-4 w-4 mr-2" />
              Send Test Email
            </Button>
          </div>
        </div>
        
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                General Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="site-name">Site Name</Label>
                  <Input 
                    id="site-name" 
                    value={formData.site_name}
                    onChange={(e) => updateFormData('site_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-email">Support Email</Label>
                  <Input 
                    id="support-email" 
                    type="email" 
                    value={formData.support_email}
                    onChange={(e) => updateFormData('support_email', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="site-description">Site Description</Label>
                <Textarea 
                  id="site-description" 
                  value={formData.site_description}
                  onChange={(e) => updateFormData('site_description', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Two-Factor Authentication Required</Label>
                  <p className="text-sm text-muted-foreground">Require 2FA for all admin accounts</p>
                </div>
                <Switch 
                  checked={formData.two_factor_required}
                  onCheckedChange={(checked) => updateFormData('two_factor_required', checked)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password-expiry">Password Expiry (days)</Label>
                  <Input 
                    id="password-expiry" 
                    type="number"
                    value={formData.password_expiry_days}
                    onChange={(e) => updateFormData('password_expiry_days', parseInt(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-attempts">Max Login Attempts</Label>
                  <Input 
                    id="max-attempts" 
                    type="number"
                    value={formData.max_login_attempts}
                    onChange={(e) => updateFormData('max_login_attempts', parseInt(e.target.value))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>New Application Alerts</Label>
                  <p className="text-sm text-muted-foreground">Email when new applications are submitted</p>
                </div>
                <Switch 
                  checked={formData.new_application_alerts}
                  onCheckedChange={(checked) => updateFormData('new_application_alerts', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Quote Request Notifications</Label>
                  <p className="text-sm text-muted-foreground">Notify when quotes are requested</p>
                </div>
                <Switch 
                  checked={formData.quote_request_notifications}
                  onCheckedChange={(checked) => updateFormData('quote_request_notifications', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>System Health Alerts</Label>
                  <p className="text-sm text-muted-foreground">Alert on system performance issues</p>
                </div>
                <Switch 
                  checked={formData.system_health_alerts}
                  onCheckedChange={(checked) => updateFormData('system_health_alerts', checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Data Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="backup-frequency">Backup Frequency</Label>
                  <Select 
                    value={formData.backup_frequency} 
                    onValueChange={(value) => updateFormData('backup_frequency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retention-period">Data Retention (days)</Label>
                  <Input 
                    id="retention-period" 
                    type="number" 
                    value={formData.data_retention_days}
                    onChange={(e) => updateFormData('data_retention_days', parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-purge Old Data</Label>
                  <p className="text-sm text-muted-foreground">Automatically remove data older than retention period</p>
                </div>
                <Switch 
                  checked={formData.auto_purge_enabled}
                  onCheckedChange={(checked) => updateFormData('auto_purge_enabled', checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                User Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-lg">
                <div>
                  <Label className="text-destructive">Clear All User Sessions</Label>
                  <p className="text-sm text-muted-foreground">This will log out all users immediately</p>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={confirmClearSessions}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Clear Sessions
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={handleReset} disabled={isSaving}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset Changes
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;